import { Chip, ErrorChip, ListItem } from '@tableau/widgets';
import * as React from 'react';

import { IGroup } from '~src/ts/ServerApi/types/IGroup';
import { IUserWithPublicInfo } from '~src/ts/ServerApi/types/IUser';

import { Messages } from '../../codegen/Localize';
import { WarningIcon } from '../img/WarningIcon';
import * as styles from '../styles/PickerStyle';
import { GroupRecipient, Recipient, UserRecipient } from '../types/Recipient';
import { compareRecipients, searchGroups, searchUsers } from '../utils/RecipientPickerUtils';
import { AsyncPicker } from './AsyncPicker';


export interface RecipientPickerProps {
  readonly label: string;
  readonly placeholder: string;
  readonly onRecipientsChanged?: (recipients: Recipient[]) => void;
  readonly onTextChanged?: (text: string) => void;
  readonly initialUsers?: IUserWithPublicInfo[];
  readonly initialGroups?: IGroup[];
}

export interface RecipientPickerState {
  readonly recipients: Recipient[];
  readonly errorMessage?: string;
}

export const ErrorDivId = 'recipient-picker-recipient-error';
export const REQUEST_MAX_ITEMS: number = 10;
export const MAX_NUM_RECIPIENTS: number = 50;
export const DEBOUNCE_TIMEOUT_MS: number = 200;

export class RecipientPicker extends React.PureComponent<RecipientPickerProps, RecipientPickerState> {

  public state: RecipientPickerState = {
    recipients: this.usersAndGroupsToRecipients(this.props.initialUsers, this.props.initialGroups),
  };

  public render(): JSX.Element {
    return (
      <div>
        {this.chipPicker()}
        <div style={styles.errorDiv} id={ErrorDivId} aria-live='polite'>
          {this.state.errorMessage &&
            <WarningIcon style={styles.icon} aria-hidden={true} />
          }
          {this.state.errorMessage}
        </div>
      </div >
    );
  }

  private chipPicker(): JSX.Element {
    return <AsyncPicker<Recipient>
      label={this.props.label}
      placeholder={this.props.placeholder}
      recipients={this.state.recipients}
      fetchRecipientsCallback={(searchTerm) => this.fetchRecipients(searchTerm)}
      onRecipientAdd={this.onValueAdd}
      onRecipientsChange={this.onValuesChange}
      recipientToListItem={this.recipientToListItem}
      recipientToStringId={this.recipientToStringId}
      recipientToChip={this.recipientToChip}
      onFilterChanged={this.props.onTextChanged}
      debounceTimeout={DEBOUNCE_TIMEOUT_MS}
    />;
  }

  public fetchRecipients(searchTerm: string): Promise<Recipient[]> {
    return Promise.all([searchUsers(searchTerm, REQUEST_MAX_ITEMS), searchGroups(searchTerm, REQUEST_MAX_ITEMS)]).then(
      ([users, groups]) => this.usersAndGroupsToRecipients(users, groups));
  }


  private recipientToChip = (recipient: Recipient): JSX.Element => {
    return recipient.kind === 'USER' ? this.userRecipientToChip(recipient) : this.groupRecipientToChip(recipient);
  }

  private groupRecipientToChip = (group: GroupRecipient): JSX.Element => {
    return (
        <Chip
          key={this.groupRecipientToStringId(group)}
          value={group.name}
          className='user-picker-chip user-picker-chip-normal'
          tooltip={group.name}
        >
          {group.name}
        </Chip>
    );
  }

  private userRecipientToChip = (user: UserRecipient): JSX.Element => {
    if (user.hasEmail) {
      return (
        <Chip
          key={this.userRecipientToStringId(user)}
          value={user.username}
          className='user-picker-chip user-picker-chip-normal'
          tooltip={user.displayName}
        >
          {user.displayName}
        </Chip>
      );
    } else {
      return (
        <ErrorChip
          key={this.userRecipientToStringId(user)}
          value={user.username}
          className='user-picker-chip user-picker-chip-error'
          message={Messages.peoplePickerErrorNoEmail({NAME: user.displayName})}
          tooltip={Messages.peoplePickerErrorNoEmail({NAME: user.displayName})}
          aria-describedby={ErrorDivId}
          aria-errormessage={ErrorDivId}
        >
          {user.displayName}
        </ErrorChip>
      );
    }
  }

  public recipientToListItem = (recipient: Recipient): ListItem => {
    return recipient.kind === 'USER' ? this.userRecipientToListItem(recipient) : this.groupRecipientToListItem(recipient);
  }

  public groupRecipientToListItem = (group: GroupRecipient): ListItem => {
    return {
      content: group.name,
      testId: 'recipient-listitem-' + group.name,
      value: this.groupRecipientToStringId(group)
    };
  }

  public userRecipientToListItem = (user: UserRecipient): ListItem => {
    const listItemTextToDisplay = user.displayName ? Messages.peoplePickerListItemFormat(
      {
        FULL_NAME: user.displayName,
        USER_NAME: user.username
      })
      : user.username;

    return {
      content: listItemTextToDisplay,
      testId: 'recipient-listitem-' + user.username,
      value: this.userRecipientToStringId(user)
    };
  }

  private recipientToStringId = (recipient: Recipient) => recipient.kind === 'USER' ? this.userRecipientToStringId(recipient) : this.groupRecipientToStringId(recipient);
  private userRecipientToStringId = (user: UserRecipient) => `user-${user.username}`;
  private groupRecipientToStringId = (group: GroupRecipient) => `group-${group.name}`;

  private addRecipientToList = (recipient: Recipient, targetIndex: number): void => {
    this.setState(
      (state) => {
        const recipients = [...state.recipients];
        recipients.splice(targetIndex, 0, recipient);  // insert new recipient at target index

        let errorMessage = state.errorMessage;
        if (recipient.kind === 'USER' && !recipient.hasEmail) {  // users without email are invalid
          errorMessage = Messages.peoplePickerErrorNoEmail({NAME: recipient.displayName});
        }

        return { recipients: recipients, errorMessage: errorMessage };
      },
      () => {
        if (this.props.onRecipientsChanged) {
          this.props.onRecipientsChanged([...this.state.recipients]);
        }
      }
    );
  }

  private onValueAdd = (recipient: Recipient, targetIndex: number): void => {
    if (this.state.recipients.length < MAX_NUM_RECIPIENTS) {
      this.addRecipientToList(recipient, targetIndex);
    }
  }

  // Invoked when chips are rearranged or deleted.
  private onValuesChange = (recipients: Recipient[]): void => {
    this.setState(
      {
        recipients: recipients,
        errorMessage: '',
      },
      () => {
        if (this.props.onRecipientsChanged) {
          this.props.onRecipientsChanged([...this.state.recipients]);
        }
      });
  }

  private usersAndGroupsToRecipients(users: IUserWithPublicInfo[], groups: IGroup[]): Recipient[] {
    const userRecipients: UserRecipient[] = users.map(user => ({ kind: 'USER', ...user}));
    const groupRecipients: GroupRecipient[] = groups.map(group => ({ kind: 'GROUP', ...group }));
    const recipients: Recipient[] = [...userRecipients, ...groupRecipients];
    recipients.sort(compareRecipients);
    return recipients;
  }
}
